<template>
    <b-container fluid class="px-md-5">
        <h5 class="mt-4 p-2 text-center text-primary text-uppercase">Cuenta Corriente</h5>
        <h2 class="mt-2 p-2 text-center text-primary text-uppercase">Saldo: {{ Number(statistics.amount).toLocaleString("en-US") + ' ' + statistics.currency }}</h2>
        <b-row class="justify-content-center">
            <b-col cols="12" md="4" align-self="center" class="mt-2">
                <b-form-group :label="labels.start+':'">
                    <b-form-datepicker
                        name="start"
                        v-model="form.start"
                        v-bind="labels['es'] || {}"
                        v-validate="'required:true|date_format:yyyy-MM-dd'"
                        :state="validateState('start')"
                        :data-vv-as="(labels.start).toLowerCase()"
                        :disabled="loading"
                        aria-describedby="start-live-feedback"
                        locale="es"
                        :min="minDate"
                        :max="maxDate"
                        hide-header
                        no-flip
                        class="text-left"
                    ></b-form-datepicker>
                    <b-form-invalid-feedback id="start-live-feedback">{{ veeErrors.first('start') }}</b-form-invalid-feedback>
                </b-form-group>
            </b-col>
            <b-col cols="12" md="4" align-self="center" class="mt-2">
                <b-form-group :label="labels.end+':'">
                    <b-form-datepicker
                        name="end"
                        v-model="form.end"
                        v-bind="labels['es'] || {}"
                        v-validate="'required:true|date_format:yyyy-MM-dd'"
                        :state="validateState('end')"
                        :data-vv-as="(labels.end).toLowerCase()"
                        :disabled="loading"
                        aria-describedby="end-live-feedback"
                        locale="es"
                        :min="minDate"
                        :max="maxDate"
                        hide-header
                        no-flip
                        class="text-left"
                    ></b-form-datepicker>
                    <b-form-invalid-feedback id="end-live-feedback">{{ veeErrors.first('end') }}</b-form-invalid-feedback>
                </b-form-group>
            </b-col>
            <b-col cols="12" md="2" align-self="center" class="mt-2">
                <b-button block variant="default" class="shadow" @click="getIni">
                    <i class="material-icons">search</i>
                    <span>Filtrar</span>
                </b-button>
            </b-col>
            <b-col cols="12" md="2" align-self="center" class="mt-2">
                <b-button block variant="success" class="shadow" @click="getDownload">
                    <i class="material-icons">download</i>
                    <span>Descargar</span>
                </b-button>
            </b-col>
        </b-row>
        <b-row class="justify-content-center">
            <b-col cols="12" class="mt-4 mb-2">
                <b-form-group class="mb-0">
                    <b-input-group size="sm">
                        <b-form-input id="search" v-model="search" type="search" placeholder="Buscar" class="pad-3-15 btlr-30 bblr-30"></b-form-input>

                        <b-input-group-append>
                            <b-button variant="default" :disabled="!search" @click="search = ''" class="m-0 btlr-0 bblr-0">
                                <i class="material-icons mdi mdi-close"></i>
                            </b-button>
                        </b-input-group-append>
                    </b-input-group>
                </b-form-group>
            </b-col>
            <b-col cols="12" class="px-0">
                <b-table
                    :items="items"
                    :fields="fields"
                    responsive="sm"
                    class="my-2 border-bottom"
                    stacked="sm"
                    hover
                    small
                    show-empty
                    no-border-collapse
                    table-variant="light"
                    details-td-class="px-0"
                    empty-filtered-text="No hay transacciones que coincidan con la búsqueda"
                    empty-text="No hay transacciones para mostrar"
                    :current-page="currentPage"
                    :per-page="perPage"
                    :filter="search"
                    :busy="isBusy"
                    @filtered="onFiltered">
                    <template #table-busy>
                        <div class="text-center text-danger my-4">
                            <b-spinner class="align-middle"></b-spinner>
                            <strong>Actualizando...</strong>
                        </div>
                    </template>

                    <template #cell(int_id)="row">
                        {{ row.item.payer === 'REMITEE' && row.item.int_id !== null ? '**' + (row.item.int_id).slice(-8) : row.item.int_id }}
                    </template>

                    <template #cell(total_text)="row">
                        <template v-if="row.item.type === '+'">
                            <b class="text-success">{{ row.item.total_text }}</b>
                        </template>
                        <template v-else>
                            <b class="text-danger">{{ row.item.total_text }}</b>
                        </template>
                    </template>

                    <template #cell(balance_text)="row">
                        <b class="text-black">{{ row.item.balance_text }}</b>
                    </template>
                </b-table>
            </b-col>
            <b-col sm="5" md="6">
                <b-form-group
                    label="Mostrar:"
                    label-for="per-page-select"
                    label-cols-sm="4"
                    label-cols-md="6"
                    label-cols-lg="9"
                    label-align-sm="right"
                    label-size="sm"
                    class="mt-2 mb-8">
                    <b-form-select
                        id="per-page-select"
                        v-model="perPage"
                        :options="pageOptions"
                        size="sm"
                    ></b-form-select>
                </b-form-group>
            </b-col>
            <b-col sm="7" md="6">
                <b-pagination
                    v-model="currentPage"
                    :total-rows="totalRows"
                    :per-page="perPage"
                    align="fill"
                    size="sm"
                    class="mt-2 mb-8"
                ></b-pagination>
            </b-col>
        </b-row>
    </b-container>
</template>

<script>
import axios from "axios";

export default {
    name: "Movements",
    props: ['user'],
    data: () => ({
        isBusy: true,
        search: '',
        fields: [
            { key: 'date', label: 'Fecha', sortable: false },
            { key: 'payment_id', label: 'ID', sortable: false, thClass: 'text-sm-right', tdClass: 'text-sm-right' },
            { key: 'int_id', label: 'Código', sortable: false, thClass: 'text-sm-right', tdClass: 'text-sm-right' },
            { key: 'payer', label: 'Corresponsal', sortable: false },
            { key: 'branch_name', label: 'Sucursal', sortable: false },
            { key: 'cashier_name', label: 'Cajero', sortable: false },
            { key: 'country_receiver', label: 'Destino', sortable: false },
            { key: 'status', label: 'Estado', sortable: false },
            { key: 'base_text', label: 'Monto Enviado', sortable: false, thClass: 'text-sm-right', tdClass: 'text-sm-right' },
            { key: 'fee_text', label: 'Tarifa Cliente', sortable: false, thClass: 'text-sm-right', tdClass: 'text-sm-right' },
            { key: 'amount_text', label: 'Giro Total', sortable: false, thClass: 'text-sm-right', tdClass: 'text-sm-right' },
            { key: 'refund', label: 'Devolucion', sortable: false, thClass: 'text-sm-right', tdClass: 'text-sm-right' },
            { key: 'prefunding', label: 'Prefondeo', sortable: false, thClass: 'text-sm-right', tdClass: 'text-sm-right' },
            { key: 'balance_text', label: 'Saldo', sortable: false, thClass: 'text-sm-right', tdClass: 'text-sm-right' },
            { key: 'gain_text', label: 'Comision Agente', sortable: false, thClass: 'text-sm-right', tdClass: 'text-sm-right' },
            { key: 'promo_code', label: 'Código Promo', sortable: false, thClass: 'text-center', tdClass: 'text-center' }
        ],
        totalRows: 1,
        currentPage: 1,
        perPage: 25,
        pageOptions: [25, 50, 100, { value: -1, text: "Todos" }],
        limit: 20,
        items: [],
        statistics: {
            company: '',
            amount: 0,
            currency: 'USD'
        },
        loading: false,
        labels: {
            start: 'Fecha Inicial',
            end: 'Fecha Final',
            es: {
                labelPrevDecade: 'Década Anterior',
                labelPrevYear: 'Año Anterior',
                labelPrevMonth: 'Mes Anterior',
                labelCurrentMonth: 'Mes Actual',
                labelNextMonth: 'Próximo Mes',
                labelNextYear: 'Próximo Año',
                labelNextDecade: 'Próxima Década',
                labelToday: 'Hoy',
                labelSelected: 'Fecha Seleccionada',
                labelNoDateSelected: 'Sin fecha seleccionada',
                labelCalendar: 'Calendario',
                labelNav: 'Navegar por el calendario',
                labelHelp: 'Use las teclas de flecha para navegar por el calendario'
            }
        },
        form: {
            start: undefined,
            end: undefined
        },
        minDate: undefined,
        maxDate: undefined
    }),
    mounted() {
        this.$emit('loader');
        this.$emit('section', 'movementspage');
        this.getDate();
        this.getIni();
    },
    watch: {
        'user.partner'() {
            clearTimeout(this.timeout);
            this.getIni();
        }
    },
    methods: {
        getIni() {
            this.$validator.validateAll().then(result => {
                if (!result) {
                    return;
                }
                axios.put('/api/partner/movements', this.form).then((res) => {
                    if (res.data.status === 'success') {
                        this.statistics = res.data.statistics;
                        this.items = res.data.items;
                    }
                }).catch(err => {
                    this.$root.$bvToast.toast(err.response.data.errors, {
                        title: 'Notificación',
                        headerClass: 'd-none',
                        variant: 'error',
                        toaster: 'b-toaster-top-right',
                        autoHideDelay: 5000,
                        solid: true,
                        appendToast: true
                    });
                }).then(() => {
                    this.isBusy = false;
                    this.totalRows = this.items.length;
                });
            });
            this.timeout = setTimeout(this.getIni, 180000);
        },
        getDate() {
            const now = new Date()
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
            // 100 años menos
            const minDate = new Date(today)
            minDate.setFullYear(minDate.getFullYear() - 100)
            // 18 años menos
            const start = new Date(today)
            start.setMonth(start.getMonth() - 1)

            this.minDate = minDate;
            this.maxDate = today;
            this.form.start = start.toISOString().slice(0, 10);
            this.form.end = today.toISOString().slice(0, 10);
        },
        getDownload() {
            axios.get('/api/export/reports/partner/' + this.user.partner + '/' + this.form.start + '/' + this.form.end, {
                responseType: 'blob'
            }).then((res) => {
                const blob = new Blob([res.data], {type: res.data.type});
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                const filename = res.headers['content-disposition'].split('filename=')[1];
                const name = 'Reporte Movimientos ' + filename.replace(/"/gi, '');
                link.setAttribute('download', name);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            });
        },
        onFiltered(filteredItems) {
            this.totalRows = filteredItems.length;
            this.currentPage = 1;
        },
        validateState(ref) {
            if (this.veeFields[ref] && (this.veeFields[ref].dirty || this.veeFields[ref].validated)) {
                return !this.veeErrors.has(ref);
            }
            return null;
        },
        resetValidationState() {
            this.isValid = undefined
        }
    },
    destroyed() {
        clearTimeout(this.timeout);
    }
}
</script>

<style scoped>

</style>
